import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowDownLeft,
  ArrowLeft,
  BadgeIndianRupee,
  CalendarDays,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import { getTransferDetails } from "../../../../Redux/Fund-Transfer-api/slice";
import "../WalletHistory.css";

const formatCurrency = (value?: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const formatDate = (dateString?: string) =>
  dateString
    ? new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const statusClass = (status?: string) =>
  `wallet-hist-status wallet-hist-status-${(status || "pending")
    .toLowerCase()
    .replace(/[^a-z]+/g, "")}`;

const RecievedFudsDetails = () => {
  const { transferNumber } = useParams<{ transferNumber: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentTransfer, loading, error } = useSelector(
    (state: RootState) => state.transfer,
  );

  useEffect(() => {
    if (transferNumber) {
      dispatch(getTransferDetails(transferNumber));
    }
  }, [dispatch, transferNumber]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">error</div>
      </div>
    );
  }

  if (!currentTransfer) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">No details found</div>
      </div>
    );
  }

  return (
    <section className="wallet-hist-page">
      <div className="wallet-hist-shell">
        <header className="wallet-hist-header">
          <div className="wallet-hist-header-left">
            <button
              type="button"
              className="wallet-hist-back-button"
              onClick={() => navigate("/RecievedFunds")}
              aria-label="Back to Received Funds"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <span className="wallet-hist-eyebrow">MY WALLET</span>
              <h1>Received Funds Details</h1>
              <p>Complete information about this received transfer.</p>
            </div>
          </div>
          <span className={statusClass(currentTransfer.status)}>
            {currentTransfer.status}
          </span>
        </header>

        <div className="wallet-hist-detail-grid">
          <section className="wallet-hist-detail-card wallet-hist-detail-wide">
            <div className="wallet-hist-detail-heading">
              <CalendarDays size={20} />
              <div>
                <span>TRANSFER INFORMATION</span>
                <h2>Transaction Record</h2>
              </div>
            </div>
            <div className="wallet-hist-detail-kv-grid">
              <div>
                <span>Transaction No</span>
                <strong>{currentTransfer.transferNumber}</strong>
              </div>
              <div>
                <span>Date</span>
                <strong>{formatDate(currentTransfer.createdAt)}</strong>
              </div>
              <div>
                <span>Incentive Type</span>
                <strong>{currentTransfer.component}</strong>
              </div>
              <div>
                <span>Status</span>
                <span className={statusClass(currentTransfer.status)}>
                  {currentTransfer.status}
                </span>
              </div>
            </div>
          </section>

          <section className="wallet-hist-detail-card">
            <div className="wallet-hist-detail-heading">
              <UserRound size={20} />
              <div>
                <span>SENDER INFORMATION</span>
                <h2>Received From</h2>
              </div>
            </div>
            <div className="wallet-hist-detail-party-row">
              <span className="wallet-hist-detail-party-avatar">
                <UserRound size={20} />
              </span>
              <div>
                <span>SENDER PARTNER</span>
                <strong>{currentTransfer.senderName || "N/A"}</strong>
                <small>{currentTransfer.senderUserId}</small>
              </div>
            </div>
            <div className="wallet-hist-detail-kv-grid">
              {currentTransfer.remarks && (
                <div>
                  <span>Incentive Name</span>
                  <strong>{currentTransfer.remarks}</strong>
                </div>
              )}
            </div>
          </section>

          <section className="wallet-hist-detail-card">
            <div className="wallet-hist-detail-heading">
              <BadgeIndianRupee size={20} />
              <div>
                <span>AMOUNT INFORMATION</span>
                <h2>Received Amount</h2>
              </div>
            </div>
            <div className="wallet-hist-detail-highlight">
              <span>RECEIVED AMOUNT</span>
              <strong>
                {formatCurrency(
                  currentTransfer.netAmount ?? currentTransfer.amount,
                )}
              </strong>
              <small>
                Gross sent: {formatCurrency(currentTransfer.amount)}
              </small>
            </div>
            <div className="wallet-hist-detail-kv-grid">
              <div>
                <span>Admin Charges</span>
                <strong>
                  {formatCurrency(currentTransfer.adminChargesAmount ?? 0)}
                </strong>
                <small>{currentTransfer.adminChargesPercent ?? 0}% fee</small>
              </div>
              <div>
                <span>Sent Amount</span>
                <strong>{formatCurrency(currentTransfer.amount)}</strong>
              </div>
            </div>
          </section>
        </div>

        <section className="wallet-hist-detail-footer-card">
          <div>
            <ShieldCheck size={20} />
            <div>
              <strong>Transfer record secured</strong>
              <span>
                This transfer is linked to your Salar wallet transaction
                history.
              </span>
            </div>
          </div>
          <button type="button" onClick={() => navigate("/RecievedFunds")}>
            <ArrowDownLeft size={15} />
            Back to Received Funds
          </button>
        </section>
      </div>
    </section>
  );
};

export default RecievedFudsDetails;
